import React from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { formatNumber } from '@utils/usajiliFormat';

/** Summary table — totals per county under the global county/date filters. */
export const USAJILISummaryTab: React.FC = () => {
  const { filters, refetchKey } = useUSAJILI();

  const { data, isLoading, error } = useQuery({
    queryKey: ['usajili_summary', filters, refetchKey],
    queryFn: () => usajiliApi.summary(filters),
  });

  const rows = data?.rows || [];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Typography sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.3, color: 'text.primary' }}>
        SUMMARY TABLE
      </Typography>

      {isLoading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
      {error && <Alert severity="error" sx={{ m: 2 }}>{(error as any)?.error?.message || 'Failed to load'}</Alert>}

      {!isLoading && !error && (
        <Paper sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table
            size="small"
            stickyHeader
            sx={{ '& td, & th': { py: 0.35, fontSize: '0.78rem' } }}
          >
            <TableHead>
              <TableRow>
                {['County', '# New IDs', '# ID Replacements', '# Voters Registered'].map((h, i) => (
                  <TableCell
                    key={h}
                    align={i === 0 ? 'left' : 'right'}
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'background.default',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      color: 'text.primary',
                      fontSize: '0.78rem',
                      letterSpacing: 0.3,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => {
                const isGrand = !!r.is_grand_total;
                return (
                  <TableRow
                    key={idx}
                    sx={{
                      ...(isGrand
                        ? {
                            bgcolor: 'background.default',
                            // Pinned to the bottom so the total stays visible when the table scrolls.
                            '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider', position: 'sticky', bottom: 0, zIndex: 1, bgcolor: 'background.default' },
                          }
                        : {}),
                      '&:hover': isGrand ? {} : { bgcolor: 'rgba(0,208,132,0.04)' },
                    }}
                  >
                    <TableCell sx={{ fontWeight: isGrand ? 700 : 400, color: isGrand ? 'primary.main' : 'text.primary' }}>
                      {r.county}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: isGrand ? 700 : 400 }}>{formatNumber(r.new_ids)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: isGrand ? 700 : 400 }}>{formatNumber(r.id_replacements)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: isGrand ? 700 : 400, color: isGrand ? 'primary.main' : 'text.primary' }}>
                      {formatNumber(r.voters_registered)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No data for the selected filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
